import { useEffect, useState, useCallback } from "react";
import { Card, Button, Cluster, Drawer, Input, Stack, EmptyState, useToast } from "@salesapp/ui";
import { crmApi, type Activity } from "../../lib/crmApi";
import { reportSaveFailure } from "../../lib/saveNotice";
import { ACTIVITY_OUTCOMES, FIELD_HINTS } from "../../help/fieldHints";
import { activityTypesHint, labelForActivityType } from "../../lib/activityTypes";
import { useLoggedActivityTypes } from "../../lib/useActivityTypes";
import { ListWorkspace, DeleteRecordButton } from "./RecordWorkspace";
import { useAccess } from "../../shell/AccessProvider";
import { TabFieldOptionsSettings } from "./TabFieldOptionsSettings";

export function ActivitiesPage({ orgId }: { orgId: string }) {
  const { show } = useToast();
  const { can } = useAccess();
  const canConfigureOptions = can("crm.field_options", "configure");
  const activityTypes = useLoggedActivityTypes(orgId);
  const typeHint = activityTypesHint(activityTypes);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    activity_type: "", subject: "", body: "", company_id: "", contact_id: "",
    opportunity_id: "", lead_id: "", occurred_at: "", outcome: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await crmApi.listActivities(orgId, { activityType: typeFilter || undefined });
      setActivities(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load activities");
    } finally {
      setLoading(false);
    }
  }, [orgId, typeFilter]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    try {
      await crmApi.createActivity(
        { activity_type: formData.activity_type || null, subject: formData.subject || null,
          body: formData.body || null, company_id: formData.company_id || null,
          contact_id: formData.contact_id || null, opportunity_id: formData.opportunity_id || null,
          lead_id: formData.lead_id || null, occurred_at: formData.occurred_at || null,
          outcome: formData.outcome || null },
        orgId,
      );
      setShowForm(false);
      setFormData({ activity_type: "", subject: "", body: "", company_id: "", contact_id: "", opportunity_id: "", lead_id: "", occurred_at: "", outcome: "" });
      show("Saved!", "success");
      load();
    } catch (e) {
      setError(reportSaveFailure(show, e, "Failed to create activity"));
    }
  };

  return (
    <ListWorkspace
      title="Activities"
      actions={
        <Cluster gap={2}>
          {canConfigureOptions ? (
            <Button type="button" variant="secondary" onClick={() => setSettingsOpen(true)}>
              Activity settings
            </Button>
          ) : null}
          <Button onClick={() => setShowForm(!showForm)}>
            {showForm ? "Cancel" : "Log activity"}
          </Button>
        </Cluster>
      }
      toolbar={
        <div className="crm-toolbar">
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="crm-select"
            aria-label="Filter by type"
            title={typeHint}
          >
            <option value="">All types</option>
            {typeFilter === "call" || !activityTypes.some((item) => item.value === "call") ? (
              <option value="call">Call</option>
            ) : null}
            {activityTypes.map((item) => (
              <option key={item.value} value={item.value} title={item.meaning}>
                {item.label}
              </option>
            ))}
          </select>
        </div>
      }
    >
      {error && <Card className="crm-error">{error}</Card>}
      {showForm && (
        <Card>
          <Stack gap={3}>
            <label className="crm-field">
              <span>Type</span>
              <select
                className="crm-select"
                aria-label="Type"
                title={typeHint}
                value={formData.activity_type}
                onChange={(e) => setFormData({ ...formData, activity_type: e.target.value })}
              >
                <option value="">Select a type</option>
                {formData.activity_type && !activityTypes.some((item) => item.value === formData.activity_type) ? (
                  <option value={formData.activity_type}>
                    {labelForActivityType(formData.activity_type, activityTypes)}
                  </option>
                ) : null}
                {activityTypes.map((item) => (
                  <option key={item.value} value={item.value} title={item.meaning}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>
            <Input label="Subject" tooltip={FIELD_HINTS.activity_subject} value={formData.subject} onChange={(e) => setFormData({ ...formData, subject: e.target.value })} />
            <label className="crm-field">
              <span>Details / comments</span>
              <textarea
                className="crm-textarea crm-textarea--activity"
                value={formData.body}
                onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                rows={6}
              />
            </label>
            <Input label="Company ID" tooltip={FIELD_HINTS.opportunity_company} value={formData.company_id} onChange={(e) => setFormData({ ...formData, company_id: e.target.value })} />
            <Input label="Contact ID" tooltip="Person this activity was with, if known." value={formData.contact_id} onChange={(e) => setFormData({ ...formData, contact_id: e.target.value })} />
            <Input label="Opportunity ID" tooltip="Deal this activity relates to, if known." value={formData.opportunity_id} onChange={(e) => setFormData({ ...formData, opportunity_id: e.target.value })} />
            <Input label="Occurred At" tooltip={FIELD_HINTS.activity_due} value={formData.occurred_at} onChange={(e) => setFormData({ ...formData, occurred_at: e.target.value })} placeholder="ISO datetime" />
            <label className="crm-field">
              <span>Outcome</span>
              <select
                className="crm-select"
                aria-label="Outcome"
                title={FIELD_HINTS.activity_outcome}
                value={formData.outcome}
                onChange={(e) => setFormData({ ...formData, outcome: e.target.value })}
              >
                <option value="">No outcome</option>
                {ACTIVITY_OUTCOMES.map((item) => (
                  <option key={item.value} value={item.value}>{item.label}</option>
                ))}
              </select>
            </label>
            <div><Button onClick={handleCreate}>Log Activity</Button></div>
          </Stack>
        </Card>
      )}
      {loading ? (
        <Card>Loading...</Card>
      ) : activities.length === 0 ? (
        <Card><EmptyState title="No activities found" description="Log your first activity to get started." /></Card>
      ) : (
        <Card className="crm-table-card">
          <table className="crm-table">
            <thead><tr><th>Type</th><th>Subject</th><th>Occurred At</th><th>Outcome</th><th className="crm-table-actions-col">Actions</th></tr></thead>
            <tbody>
              {activities.map((a) => (
                <tr key={a.id}>
                  <td>{labelForActivityType(a.activity_type, activityTypes)}</td>
                  <td>{a.subject ?? "\u2014"}</td>
                  <td>{a.occurred_at ?? "\u2014"}</td>
                  <td>{a.outcome ?? "\u2014"}</td>
                  <td className="crm-table-actions">
                    <DeleteRecordButton
                      capability="crm.activities"
                      recordLabel={a.subject || "this activity"}
                      size="sm"
                      variant="ghost"
                      onDeleted={async () => {
                        await crmApi.deleteActivity(a.id, orgId);
                        await load();
                      }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
      <Drawer isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} title="Activity settings">
        <TabFieldOptionsSettings orgId={orgId} entity="activity" />
      </Drawer>
    </ListWorkspace>
  );
}
